import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Command, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 6) return setError("Password must be at least 6 characters");
    if (pw !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9FA] text-[#09090B] px-6">
      <div className="w-full max-w-sm">
        <div className="h-10 w-10 bg-primary flex items-center justify-center mb-6"><Command className="h-5 w-5 text-white" /></div>
        {!token ? (
          <div>
            <h1 className="font-heading font-bold text-2xl">Invalid link</h1>
            <p className="text-sm text-muted-foreground mt-2">This reset link is missing its token. Request a new one.</p>
            <Link to="/forgot-password" className="mt-6 inline-block text-sm text-primary hover:underline">Request a new link</Link>
          </div>
        ) : done ? (
          <div>
            <CheckCircle2 className="h-8 w-8 text-[#10B981] mb-4" />
            <h1 className="font-heading font-bold text-2xl">Password updated</h1>
            <p className="text-sm text-muted-foreground mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading font-bold text-2xl">Set a new password</h1>
            <p className="text-sm text-muted-foreground mt-2 mb-6">Choose a strong password you haven't used before.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pw">New password</Label>
                <Input id="pw" type="password" data-testid="reset-password" value={pw} onChange={(e) => setPw(e.target.value)} required className="bg-white border-black/10 rounded-none h-11" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input id="confirm" type="password" data-testid="reset-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="bg-white border-black/10 rounded-none h-11" />
              </div>
              {error && <p data-testid="reset-error" className="text-sm text-destructive bg-destructive/10 border border-destructive/30 px-3 py-2">{error}</p>}
              <Button type="submit" disabled={loading} data-testid="reset-submit" className="w-full h-11 rounded-none bg-primary hover:bg-[#09090B] transition-colors font-medium">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
